import { FunctionComponent } from "react";
import Markdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import { classNames } from "@/utils/classNames";

interface MarkdownContentProps {
	content: string;
	className?: string;
}

interface MdastNode {
	type?: string;
	spread?: boolean;
	children?: MdastNode[];
}

/**
 * Forces every list to render "tight" (no `<p>` wrapper around each item).
 *
 * AI-generated markdown routinely separates bullet items with a blank line,
 * which CommonMark/GFM treats as a *loose* list — each `<li>` then wraps its
 * text in a `<p>` that inherits the paragraph margins, producing a stray line
 * break after the marker and large gaps between items. Setting `spread = false`
 * on every `list`/`listItem` node collapses that back to `<li>text</li>`.
 */
const remarkTightLists = () => {
	const walk = (node: MdastNode): void => {
		if (node.type === "list" || node.type === "listItem") {
			node.spread = false;
		}

		node.children?.forEach(walk);
	};

	return (tree: MdastNode): void => walk(tree);
};

// Compact markdown styling for AI-generated content rendered inside panels/cards.
// Headings are flattened to the surrounding text size — the enclosing UI (section
// cards, subsection headers) already provides the visual hierarchy.
const MARKDOWN_STYLES = [
	"[&_p]:mb-2 [&_p:last-child]:mb-0 [&_p]:leading-relaxed",
	"[&_ul]:list-disc [&_ul]:pl-4 [&_ul]:mb-2 [&_ul:last-child]:mb-0",
	"[&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:mb-2 [&_ol:last-child]:mb-0",
	"[&_li]:mb-1 [&_li]:leading-relaxed",
	"[&_strong]:font-semibold [&_strong]:text-text",
	"[&_a]:text-blue-action [&_a]:underline",
	"[&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_h4]:font-semibold",
	"[&_h1]:mb-1 [&_h2]:mb-1 [&_h3]:mb-1 [&_h4]:mb-1",
	"[&_code]:font-mono [&_code]:text-xs",
	"[&_blockquote]:border-l-2 [&_blockquote]:border-border-light [&_blockquote]:pl-2",
	"[&_img]:my-2 [&_img]:max-w-full [&_img]:rounded",
].join(" ");

/**
 * Renders AI-generated markdown (deep research results, briefs, …) with compact,
 * panel-friendly styling. `remark-breaks` keeps single newlines from legacy
 * plain-text content as line breaks instead of collapsing them into one paragraph;
 * `remarkTightLists` collapses blank-line-separated (loose) bullet lists so items
 * render as compact `<li>` rows instead of `<p>`-wrapped blocks.
 */
export const MarkdownContent: FunctionComponent<MarkdownContentProps> = ({
	content,
	className,
}) => {
	return (
		<div className={classNames(MARKDOWN_STYLES, className)}>
			<Markdown remarkPlugins={[remarkGfm, remarkBreaks, remarkTightLists]}>{content}</Markdown>
		</div>
	);
};
